import {onBeforeUnmount, ref, watch, type Ref} from 'vue'
import {scriptsApi} from '../api/scripts'
import {draftOf, parseDraft, type ScriptDocument, type ScriptVersion} from '../types/scripts'

type Options = {
  script: Ref<ScriptDocument | null>; workspaceKey: Readonly<Ref<string>>;
  readOnly: Readonly<Ref<boolean>>; dirty: Readonly<Ref<boolean>>;
  saving: Readonly<Ref<boolean>>; conflict: Readonly<Ref<boolean>>;
  save: () => Promise<boolean>; closeDialog?: () => void
}
type Context = {id: string; workspace: string}

/** History requests belong to one open dialog, script and authenticated space. */
export function useScriptHistory(options: Options) {
  const items = ref<ScriptVersion[]>([]), versionPreview = ref<ScriptDocument | null>(null)
  const busy = ref(false), error = ref('')
  let generation = 0, opened = false, disposed = false, previewContext: Context | null = null
  const context = (): Context | null => options.script.value ? {id: options.script.value.id, workspace: options.workspaceKey.value} : null
  const sameContext = (captured: Context) => !disposed && options.script.value?.id === captured.id && options.workspaceKey.value === captured.workspace
  const currentRequest = (captured: Context, request: number) => opened && request === generation && sameContext(captured)
  const message = (cause: unknown) => cause instanceof Error ? cause.message : '暂时无法读取历史版本。'

  function invalidate() {
    generation++; opened = false; previewContext = null
    items.value = []; versionPreview.value = null; busy.value = false; error.value = ''
  }
  watch(() => `${options.workspaceKey.value}\0${options.script.value?.id || ''}`, invalidate, {flush: 'sync'})
  onBeforeUnmount(() => {disposed = true; invalidate()})

  async function open(): Promise<boolean> {
    const captured = context()
    if (!captured || disposed) return false
    invalidate(); opened = true
    const request = ++generation
    busy.value = true
    try {
      const result = await scriptsApi.history(captured.id)
      if (!currentRequest(captured, request)) return false
      if (!Array.isArray(result.items) || !result.items.every(item => Number.isInteger(item?.revision) && item.revision > 0 && typeof item.updatedAt === 'number' && Number.isFinite(new Date(item.updatedAt).getTime()) && typeof item.editorName === 'string')) throw new Error('历史版本数据无效，请重新读取。')
      items.value = result.items.map(item => ({revision: item.revision, updatedAt: item.updatedAt, editorName: item.editorName}))
      return true
    } catch (cause) {
      if (currentRequest(captured, request)) error.value = message(cause)
      return false
    } finally {if (currentRequest(captured, request)) busy.value = false}
  }

  async function preview(revision: number): Promise<boolean> {
    const captured = context()
    if (!captured || !opened || disposed || !Number.isInteger(revision) || revision < 1) return false
    const request = ++generation
    busy.value = true; error.value = ''; versionPreview.value = null; previewContext = null
    try {
      const result = await scriptsApi.version(captured.id, revision)
      if (!currentRequest(captured, request)) return false
      const doc = result.script, draft = parseDraft(doc)
      if (!doc || doc.id !== captured.id || doc.revision !== revision || !draft) throw new Error('历史内容与当前脚本不一致，请重新读取。')
      versionPreview.value = {...doc, ...draft}; previewContext = captured
      return true
    } catch (cause) {
      if (currentRequest(captured, request)) error.value = message(cause)
      return false
    } finally {if (currentRequest(captured, request)) busy.value = false}
  }

  async function useVersion(): Promise<boolean> {
    const doc = options.script.value, selected = versionPreview.value, captured = previewContext
    if (!doc?.canEdit || !selected || !captured || !opened || busy.value || !sameContext(captured) || selected.id !== doc.id || options.readOnly.value || options.dirty.value || options.saving.value || options.conflict.value || selected.revision === doc.revision) return false
    const draft = parseDraft(draftOf(selected))
    if (!draft) {error.value = '历史内容无效，请重新读取。'; return false}
    try {
      // Close the modal before changing the visible document; invalidate all
      // pending history responses so none can reintroduce the selected version.
      options.closeDialog?.(); invalidate()
      if (!sameContext(captured) || options.readOnly.value || options.dirty.value || options.saving.value || options.conflict.value) return false
      options.script.value = {...doc, ...draft}
      const saved = await options.save()
      if (!sameContext(captured)) return false
      if (!saved) error.value = '历史内容已载入但尚未保存，请重试保存。'
      return saved
    } catch (cause) {
      if (sameContext(captured)) error.value = message(cause)
      return false
    }
  }

  return {items, versionPreview, busy, error, open, preview, useVersion, invalidate, close: invalidate}
}
